import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { 
  Shield, Award, Lock, Key, Server, Code, FileCheck, CheckCircle, 
  Download, Upload, Calendar, Clock, BadgeCheck, AlertCircle, Loader2,
  ChevronDown, ChevronUp, BarChart3, X, Globe, Copy, ExternalLink, Settings,
  FileSearch, Plus, Trash2, AlertTriangle, Link, RefreshCw, Eye, Building2, FileText, Pencil, Database, Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import CustomDomainsManager from '../components/CustomDomainsManager';

export default function TrustCenterPage() {
  const { user } = useContext(AuthContext);
  const [certifications, setCertifications] = useState([]);
  const [controlCoverage, setControlCoverage] = useState(null);
  const [securityPractices, setSecurityPractices] = useState([]);
  const [pentestReports, setPentestReports] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  
  // Public URL settings state
  const [publicSettings, setPublicSettings] = useState(null);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [editedSettings, setEditedSettings] = useState({});
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [domainVerificationResult, setDomainVerificationResult] = useState(null);
  
  // Logo upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  
  // Security practices management state
  const [practicesModalOpen, setPracticesModalOpen] = useState(false);
  const [editablePractices, setEditablePractices] = useState([]);
  const [savingPractices, setSavingPractices] = useState(false);
  
  // Upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Add certification modal state
  const [addCertModalOpen, setAddCertModalOpen] = useState(false);
  const [newCertIsCustom, setNewCertIsCustom] = useState(false);
  const [newCert, setNewCert] = useState({
    framework: '',
    status: 'In Progress',
    description: '',
    issued_date: '',
    expiry_date: ''
  });

  // Edit certification modal state
  const [editCertModalOpen, setEditCertModalOpen] = useState(false);
  const [editCertIsCustom, setEditCertIsCustom] = useState(false);
  const [editingCert, setEditingCert] = useState(null);

  // Preset framework list (used by both Add and Edit certification dialogs)
  const PRESET_FRAMEWORKS = [
    'ISO 27001:2022', 'SOC 2 Type II', 'Essential 8', 'PCI-DSS', 'HIPAA',
    'ISO 42001', 'GDPR', 'NIST CSF', 'CCPA', 'FedRAMP', 'ISO 22301', 'CSA STAR',
  ];

  // Risk Profile state
  const [riskProfileModalOpen, setRiskProfileModalOpen] = useState(false);
  const [editingRiskField, setEditingRiskField] = useState(null);
  const [riskFieldValue, setRiskFieldValue] = useState('');
  const [savingRiskProfile, setSavingRiskProfile] = useState(false);

  // Pentest report modal state
  const [addPentestModalOpen, setAddPentestModalOpen] = useState(false);
  const [uploadPentestModalOpen, setUploadPentestModalOpen] = useState(false);
  const [uploadingReport, setUploadingReport] = useState(null);
  const [pentestFile, setPentestFile] = useState(null);
  const [uploadingPentest, setUploadingPentest] = useState(false);
  // Edit pentest report + rename file state
  const [editPentestModalOpen, setEditPentestModalOpen] = useState(false);
  const [editingPentest, setEditingPentest] = useState(null);
  const [savingEditPentest, setSavingEditPentest] = useState(false);
  const [renameFileModal, setRenameFileModal] = useState(null); // { kind: 'cert'|'pentest', id, current }
  const [renameFileValue, setRenameFileValue] = useState('');
  const [savingRename, setSavingRename] = useState(false);
  const [newPentest, setNewPentest] = useState({
    title: '',
    vendor: '',
    test_date: '',
    scope: '',
    findings_summary: '',
    critical_findings: 0,
    high_findings: 0,
    medium_findings: 0,
    low_findings: 0,
    status: 'Completed'
  });

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => {
    fetchTrustCenterData();
    fetchPublicSettings();
    fetchPentestReports();
    fetchPolicies();
  }, []);

  const fetchTrustCenterData = async () => {
    try {
      const [certsRes, coverageRes, practicesRes] = await Promise.all([
        axios.get(`${API}/trust-center/certifications-v2`),
        axios.get(`${API}/trust-center/control-coverage`),
        axios.get(`${API}/trust-center/security-practices`)
      ]);

      setCertifications(certsRes.data.certifications || []);
      setControlCoverage(coverageRes.data);
      setSecurityPractices(practicesRes.data.practices || []);
    } catch (error) {
      console.error('Failed to fetch trust center data:', error);
      toast.error('Failed to load Trust Center data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPublicSettings = async () => {
    try {
      const response = await axios.get(`${API}/trust-center/settings`);
      setPublicSettings(response.data);
      setEditedSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch public settings:', error);
    }
  };

  const fetchPentestReports = async () => {
    try {
      const response = await axios.get(`${API}/trust-center/pentest-reports`);
      setPentestReports(response.data.pentest_reports || response.data.reports || []);
    } catch (error) {
      console.error('Failed to fetch pentest reports:', error);
    }
  };

  const fetchPolicies = async () => {
    try {
      // Fetch from main policies endpoint (filtered for Trust Center)
      const response = await axios.get(`${API}/policies/trust-center`);
      setPolicies(response.data.policies || []);
    } catch (error) {
      console.error('Failed to fetch policies:', error);
    }
  };

  const handleAddPentestReport = async () => {
    try {
      const params = new URLSearchParams({
        title: newPentest.title,
        vendor: newPentest.vendor,
        test_date: newPentest.test_date,
        scope: newPentest.scope,
        findings_summary: newPentest.findings_summary,
        critical_findings: newPentest.critical_findings.toString(),
        high_findings: newPentest.high_findings.toString(),
        medium_findings: newPentest.medium_findings.toString(),
        low_findings: newPentest.low_findings.toString(),
        status: newPentest.status
      });
      
      await axios.post(`${API}/trust-center/pentest-reports/add?${params.toString()}`);
      toast.success('Pentest report added successfully');
      setAddPentestModalOpen(false);
      setNewPentest({
        title: '',
        vendor: '',
        test_date: '',
        scope: '',
        findings_summary: '',
        critical_findings: 0,
        high_findings: 0,
        medium_findings: 0,
        low_findings: 0,
        status: 'Completed'
      });
      fetchPentestReports();
    } catch (error) {
      console.error('Failed to add pentest report:', error);
      toast.error(error.response?.data?.detail || 'Failed to add pentest report');
    }
  };

  const handleUploadPentestFile = async () => {
    if (!pentestFile || !uploadingReport) return;
    
    setUploadingPentest(true);
    try {
      const formData = new FormData();
      formData.append('file', pentestFile);
      
      await axios.post(
        `${API}/trust-center/pentest-reports/${uploadingReport.id}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Report file uploaded successfully');
      setUploadPentestModalOpen(false);
      setPentestFile(null);
      setUploadingReport(null);
      fetchPentestReports();
    } catch (error) {
      console.error('Failed to upload report file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload report file');
    } finally {
      setUploadingPentest(false);
    }
  };

  const handleDeletePentestReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this pentest report?')) return;
    
    try {
      await axios.delete(`${API}/trust-center/pentest-reports/${reportId}`);
      toast.success('Pentest report deleted');
      fetchPentestReports();
    } catch (error) {
      toast.error('Failed to delete pentest report');
    }
  };

  const handleDownloadPentestReport = async (report) => {
    try {
      const response = await axios.get(
        `${API}/trust-center/pentest-reports/${report.id}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', report.file_name || 'pentest_report.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download report file');
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const response = await axios.put(`${API}/trust-center/settings`, {
        is_public: editedSettings.is_public,
        public_slug: editedSettings.public_slug,
        organization_name: editedSettings.organization_name,
        hero_title: editedSettings.hero_title || null,
        hero_subtitle: editedSettings.hero_subtitle || null,
        visible_sections: editedSettings.visible_sections,
        custom_message: editedSettings.custom_message,
        contact_email: editedSettings.contact_email,
        custom_domain: editedSettings.custom_domain || null
      });
      setPublicSettings(response.data.settings);
      setEditedSettings(response.data.settings);
      setDomainVerificationResult(null); // Reset verification result
      toast.success('Settings saved successfully');
      setSettingsModalOpen(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleVerifyDomain = async () => {
    if (!editedSettings.custom_domain) {
      toast.error('Please enter a custom domain first');
      return;
    }
    
    setVerifyingDomain(true);
    setDomainVerificationResult(null);
    
    try {
      // First save the custom domain
      await axios.put(`${API}/trust-center/settings`, {
        custom_domain: editedSettings.custom_domain
      });
      
      // Then verify it
      const response = await axios.post(`${API}/trust-center/verify-domain`);
      setDomainVerificationResult(response.data);
      
      if (response.data.verified) {
        toast.success('Domain verified successfully!');
        // Refresh settings to get updated verification status
        fetchPublicSettings();
      } else {
        toast.error(response.data.message || 'Domain verification failed');
      }
    } catch (error) {
      console.error('Failed to verify domain:', error);
      setDomainVerificationResult({
        verified: false,
        message: error.response?.data?.detail || 'Failed to verify domain'
      });
      toast.error(error.response?.data?.detail || 'Failed to verify domain');
    } finally {
      setVerifyingDomain(false);
    }
  };

  // Handle logo file upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPG, SVG, or WebP');
      return;
    }
    
    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 2MB');
      return;
    }
    
    setUploadingLogo(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/trust-center/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setEditedSettings({...editedSettings, organization_logo: response.data.logo_url});
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Fetch security practices
  const fetchSecurityPractices = async () => {
    try {
      const response = await axios.get(`${API}/trust-center/security-practices`);
      setEditablePractices(response.data.practices || []);
    } catch (error) {
      console.error('Failed to fetch security practices:', error);
    }
  };

  // Save security practices
  const handleSavePractices = async () => {
    setSavingPractices(true);
    try {
      await axios.put(`${API}/trust-center/security-practices`, editablePractices);
      toast.success('Security practices saved successfully');
      setPracticesModalOpen(false);
      // Refresh main security practices
      const response = await axios.get(`${API}/trust-center/security-practices`);
      setSecurityPractices(response.data.practices || []);
    } catch (error) {
      console.error('Failed to save security practices:', error);
      toast.error('Failed to save security practices');
    } finally {
      setSavingPractices(false);
    }
  };

  // Add new practice category
  const addPracticeCategory = () => {
    setEditablePractices([
      ...editablePractices,
      {
        id: `new-${Date.now()}`,
        name: 'New Category',
        icon: 'shield',
        items: [],
        enabled: true,
        order: editablePractices.length
      }
    ]);
  };

  // Delete practice category
  const deletePracticeCategory = (categoryId) => {
    setEditablePractices(editablePractices.filter(p => p.id !== categoryId));
  };

  // Add item to category
  const addPracticeItem = (categoryId) => {
    setEditablePractices(editablePractices.map(p => {
      if (p.id === categoryId) {
        return {
          ...p,
          items: [...(p.items || []), { id: `item-${Date.now()}`, text: '', enabled: true }]
        };
      }
      return p;
    }));
  };

  // Update practice item
  const updatePracticeItem = (categoryId, itemId, updates) => {
    setEditablePractices(editablePractices.map(p => {
      if (p.id === categoryId) {
        return {
          ...p,
          items: p.items.map(item => 
            item.id === itemId ? { ...item, ...updates } : item
          )
        };
      }
      return p;
    }));
  };

  // Delete practice item
  const deletePracticeItem = (categoryId, itemId) => {
    setEditablePractices(editablePractices.map(p => {
      if (p.id === categoryId) {
        return {
          ...p,
          items: p.items.filter(item => item.id !== itemId)
        };
      }
      return p;
    }));
  };

  const copyPublicUrl = () => {
    if (publicSettings?.public_url) {
      navigator.clipboard.writeText(publicSettings.public_url);
      toast.success('URL copied to clipboard');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'certified':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'compliant':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'in progress':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'certified':
        return <BadgeCheck className="w-4 h-4" />;
      case 'compliant':
        return <CheckCircle className="w-4 h-4" />;
      case 'in progress':
        return <Clock className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getIconComponent = (iconName) => {
    const icons = {
      'shield-check': Shield,
      'award': Award,
      'lock': Lock,
      'shield': Shield,
      'key': Key,
      'server': Server,
      'code': Code,
      'file-check': FileCheck,
      'database': Database,
      'activity': Activity
    };
    const Icon = icons[iconName] || Shield;
    return <Icon className="w-6 h-6" />;
  };

  const toggleSection = (category) => {
    setExpandedSections(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleUploadCertificate = async () => {
    if (!uploadFile || !uploadingCert) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      await axios.post(`${API}/trust-center/certificates/upload?cert_id=${encodeURIComponent(uploadingCert.id)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Certificate uploaded successfully');
      setUploadModalOpen(false);
      setUploadFile(null);
      setUploadingCert(null);
      fetchTrustCenterData();
    } catch (error) {
      console.error('Failed to upload certificate:', error);
      toast.error('Failed to upload certificate');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadCertificate = async (cert) => {
    try {
      const response = await axios.get(`${API}/trust-center/certificates/download/${cert.id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${cert.framework}_certificate.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download certificate:', error);
      toast.error('Certificate not available for download');
    }
  };

  // Risk Profile handlers
  const openRiskFieldModal = (fieldKey, fieldLabel, currentValue) => {
    setEditingRiskField({ key: fieldKey, label: fieldLabel });
    setRiskFieldValue(currentValue || '');
    setRiskProfileModalOpen(true);
  };

  const handleSaveRiskField = async () => {
    if (!editingRiskField) return;
    
    setSavingRiskProfile(true);
    try {
      const params = new URLSearchParams({
        field: editingRiskField.key,
        value: riskFieldValue
      });
      
      await axios.put(`${API}/trust-center/risk-profile?${params.toString()}`);
      toast.success('Risk profile updated successfully');
      setRiskProfileModalOpen(false);
      setEditingRiskField(null);
      fetchTrustCenterData();
    } catch (error) {
      console.error('Failed to update risk profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update risk profile');
    } finally {
      setSavingRiskProfile(false);
    }
  };

  const getRiskProfile = () => {
    return publicSettings?.risk_profile || {
      data_access_level: { value: 'Public' },
      impact_level: { value: 'Moderate' },
      recovery_time_objective: { value: '10 hours' }
    };
  };

  const handleAddCertification = async () => {
    if (!newCert.framework) {
      toast.error('Please select a framework');
      return;
    }

    try {
      const params = new URLSearchParams({
        framework: newCert.framework,
        status: newCert.status,
        description: newCert.description || ''
      });
      if (newCert.issued_date) params.append('issued_date', newCert.issued_date);
      if (newCert.expiry_date) params.append('expiry_date', newCert.expiry_date);

      await axios.post(`${API}/trust-center/certifications/add?${params.toString()}`);
      toast.success('Certification added successfully');
      setAddCertModalOpen(false);
      setNewCertIsCustom(false);
      setNewCert({
        framework: '',
        status: 'In Progress',
        description: '',
        issued_date: '',
        expiry_date: ''
      });
      fetchTrustCenterData();
    } catch (error) {
      console.error('Failed to add certification:', error);
      toast.error('Failed to add certification');
    }
  };

  const handleEditCertification = async () => {
    if (!editingCert?.framework) {
      toast.error('Please select a framework');
      return;
    }

    try {
      const params = new URLSearchParams({
        framework: editingCert.framework,
        status: editingCert.status,
        description: editingCert.description || ''
      });
      if (editingCert.issued_date) params.append('issued_date', editingCert.issued_date);
      if (editingCert.expiry_date) params.append('expiry_date', editingCert.expiry_date);

      await axios.put(`${API}/trust-center/certifications/${editingCert.id}?${params.toString()}`);
      toast.success('Certification updated successfully');
      setEditCertModalOpen(false);
      setEditCertIsCustom(false);
      setEditingCert(null);
      fetchTrustCenterData();
    } catch (error) {
      console.error('Failed to update certification:', error);
      toast.error('Failed to update certification');
    }
  };

  const handleDeleteCertification = async (certId) => {
    if (!window.confirm('Are you sure you want to delete this certification?')) {
      return;
    }

    try {
      await axios.delete(`${API}/trust-center/certifications/${certId}`);
      toast.success('Certification deleted successfully');
      fetchTrustCenterData();
    } catch (error) {
      console.error('Failed to delete certification:', error);
      toast.error('Failed to delete certification');
    }
  };

  const openEditCertModal = (cert) => {
    setEditingCert({
      ...cert,
      issued_date: cert.issued_date ? cert.issued_date.split('T')[0] : '',
      expiry_date: cert.expiry_date ? cert.expiry_date.split('T')[0] : ''
    });
    // Auto-detect custom framework so admins can edit existing custom entries.
    setEditCertIsCustom(!!cert.framework && !PRESET_FRAMEWORKS.includes(cert.framework));
    setEditCertModalOpen(true);
  };

  const openEditPentestModal = (report) => {
    setEditingPentest({
      ...report,
      test_date: report.test_date ? report.test_date.split('T')[0] : '',
      critical_findings: Number(report.critical_findings || 0),
      high_findings: Number(report.high_findings || 0),
      medium_findings: Number(report.medium_findings || 0),
      low_findings: Number(report.low_findings || 0),
      status: report.status || 'Completed',
    });
    setEditPentestModalOpen(true);
  };

  const handleEditPentestReport = async () => {
    if (!editingPentest?.id) return;
    if (!editingPentest.title || !editingPentest.vendor) {
      toast.error('Title and Vendor are required');
      return;
    }
    setSavingEditPentest(true);
    try {
      await axios.patch(`${API}/trust-center/pentest-reports/${editingPentest.id}`, {
        title: editingPentest.title,
        vendor: editingPentest.vendor,
        test_date: editingPentest.test_date || null,
        scope: editingPentest.scope || '',
        findings_summary: editingPentest.findings_summary || '',
        status: editingPentest.status || 'Completed',
        critical_findings: Number(editingPentest.critical_findings || 0),
        high_findings: Number(editingPentest.high_findings || 0),
        medium_findings: Number(editingPentest.medium_findings || 0),
        low_findings: Number(editingPentest.low_findings || 0),
      });
      toast.success('Pentest report updated');
      setEditPentestModalOpen(false);
      setEditingPentest(null);
      fetchPentestReports();
    } catch (error) {
      console.error('Failed to update pentest report:', error);
      toast.error(error.response?.data?.detail || 'Failed to update pentest report');
    } finally {
      setSavingEditPentest(false);
    }
  };

  const openRenameFileModal = (kind, record) => {
    setRenameFileModal({ kind, id: record.id, label: record.title || record.framework || 'file' });
    setRenameFileValue(record.file_name || '');
  };

  const handleRenameFile = async () => {
    if (!renameFileModal) return;
    const trimmed = (renameFileValue || '').trim();
    if (!trimmed) {
      toast.error('File name cannot be empty');
      return;
    }
    setSavingRename(true);
    try {
      if (renameFileModal.kind === 'cert') {
        await axios.patch(
          `${API}/trust-center/certifications/${renameFileModal.id}/rename`,
          { file_name: trimmed }
        );
      } else {
        await axios.patch(
          `${API}/trust-center/pentest-reports/${renameFileModal.id}`,
          { file_name: trimmed }
        );
      }
      toast.success('File renamed');
      setRenameFileModal(null);
      setRenameFileValue('');
      if (renameFileModal.kind === 'cert') fetchTrustCenterData(); else fetchPentestReports();
    } catch (error) {
      console.error('Failed to rename file:', error);
      toast.error(error.response?.data?.detail || 'Failed to rename file');
    } finally {
      setSavingRename(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-12" data-testid="trust-center-page">
      {/* Hero Section */}
      <div className="bg-slate-900 text-white rounded-lg p-8 -mx-2">
        <div className="flex items-center gap-3 mb-3">
          <Shield className="w-10 h-10" />
          <h1 className="text-3xl font-heading font-bold">Trust Center</h1>
        </div>
        <p className="text-slate-300 text-lg max-w-3xl">
          Our commitment to security, privacy, and compliance. Review our certifications, 
          control coverage, and security practices.
        </p>
      </div>

      {/* Public URL Settings Card - Admin Only */}
      {isAdmin && publicSettings && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-slate-700" />
                <CardTitle className="text-lg text-slate-900">Public Trust Center</CardTitle>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setEditedSettings(publicSettings);
                  setDomainVerificationResult(null);
                  setSettingsModalOpen(true);
                }}
                data-testid="trust-center-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </div>
            <CardDescription className="text-slate-800">
              Share your Trust Center publicly with customers and partners
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-700">Status:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    publicSettings.is_public 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {publicSettings.is_public ? 'Public' : 'Private'}
                  </span>
                </div>
                {publicSettings.is_public && publicSettings.public_url && (
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-sm text-slate-600 truncate max-w-md">
                      {publicSettings.public_url}
                    </span>
                    <Button variant="ghost" size="sm" onClick={copyPublicUrl} data-testid="copy-url-btn">
                      <Copy className="w-4 h-4" />
                    </Button>
                    <a 
                      href={publicSettings.public_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-slate-600 hover:text-slate-900"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                )}
              </div>
              
              {/* Custom Domain Status */}
              {publicSettings.custom_domain && (
                <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                  <Link className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-600">Custom Domain:</span>
                  <span className="text-sm font-medium text-slate-900">{publicSettings.custom_domain}</span>
                  {publicSettings.custom_domain_verified ? (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      <CheckCircle className="w-3 h-3" />
                      Verified
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      <AlertTriangle className="w-3 h-3" />
                      Pending
                    </span>
                  )}
                  {publicSettings.custom_domain_verified && (
                    <a 
                      href={`https://${publicSettings.custom_domain}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-slate-600 hover:text-slate-900"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 1: Certifications & Compliance */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-heading font-semibold text-black">
              Certifications & Compliance
            </h2>
            <p className="text-gray-600 mt-1">
              Our compliance certifications and their current status
            </p>
          </div>
          {isAdmin && (
            <Button onClick={() => setAddCertModalOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-certification-btn">
              <Award className="w-4 h-4 mr-2" />
              Add Certification
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certifications.map((cert, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow bg-white border border-gray-200" data-testid={`cert-card-${cert.framework}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="p-2 bg-gray-100 rounded-lg text-black">
                    {getIconComponent(cert.icon)}
                  </div>
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full border flex items-center gap-1 ${getStatusColor(cert.status)}`}>
                    {getStatusIcon(cert.status)}
                    {cert.status}
                  </span>
                </div>
                <CardTitle className="text-lg mt-3 text-black">{cert.framework}</CardTitle>
                <CardDescription className="text-gray-600">{cert.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Dates */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {cert.issued_date && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Calendar className="w-4 h-4" />
                      <div>
                        <p className="text-xs text-gray-500">Issued</p>
                        <p className="font-medium text-black">{new Date(cert.issued_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                  )}
                  {cert.expiry_date && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Clock className="w-4 h-4" />
                      <div>
                        <p className="text-xs text-gray-500">Expires</p>
                        <p className="font-medium text-black">{new Date(cert.expiry_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  {cert.has_certificate && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1 border-gray-300 text-black hover:bg-gray-100"
                      onClick={() => handleDownloadCertificate(cert)}
                      data-testid={`download-cert-${cert.framework}`}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  )}
                  {isAdmin && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1 border-gray-300 text-black hover:bg-gray-100"
                      onClick={() => {
                        setUploadingCert(cert);
                        setUploadModalOpen(true);
                      }}
                      data-testid={`upload-cert-${cert.framework}`}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {cert.has_certificate ? 'Replace' : 'Upload'}
                    </Button>
                  )}
                </div>
                {/* File name with rename (visible to admins when a file exists) */}
                {isAdmin && cert.has_certificate && cert.file_name && (
                  <div
                    className="flex items-center justify-between gap-2 pt-2 text-xs text-gray-600"
                    data-testid={`cert-filename-row-${cert.id}`}
                  >
                    <span className="truncate" title={cert.file_name}>
                      <FileText className="w-3.5 h-3.5 inline-block mr-1 text-gray-400" />
                      {cert.file_name}
                    </span>
                    <button
                      type="button"
                      onClick={() => openRenameFileModal('cert', cert)}
                      className="inline-flex items-center gap-1 text-teal-700 hover:text-teal-800 hover:underline whitespace-nowrap"
                      data-testid={`rename-cert-file-${cert.id}`}
                    >
                      <Pencil className="w-3 h-3" /> Rename
                    </button>
                  </div>
                )}
                {/* Admin Edit/Delete Actions */}
                {isAdmin && cert.id && (
                  <div className="flex gap-2 pt-2 border-t border-gray-100">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1 border-gray-300 text-black hover:bg-gray-100"
                      onClick={() => openEditCertModal(cert)}
                      data-testid={`edit-cert-${cert.framework}`}
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteCertification(cert.id)}
                      data-testid={`delete-cert-${cert.framework}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Section: Pentest Reports */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-heading font-semibold text-black flex items-center gap-2">
              <FileSearch className="w-6 h-6" />
              Penetration Test Reports
            </h2>
            <p className="text-gray-600 mt-1">
              Security assessment and penetration testing documentation
            </p>
          </div>
          {isAdmin && (
            <Button onClick={() => setAddPentestModalOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-pentest-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Pentest Report
            </Button>
          )}
        </div>

        {pentestReports.length === 0 ? (
          <Card className="bg-white border border-gray-200">
            <CardContent className="py-12 text-center">
              <FileSearch className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600">No pentest reports added yet</p>
              {isAdmin && (
                <Button variant="outline" onClick={() => setAddPentestModalOpen(true)} className="mt-4">
                  Add Your First Report
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pentestReports.map((report) => (
              <Card key={report.id} className="bg-white border border-gray-200" data-testid={`pentest-card-${report.id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-slate-900">{report.title}</h3>
                      <p className="text-sm text-slate-600">{report.vendor}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      report.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {report.status}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Calendar className="w-4 h-4" />
                      <span>Test Date: {report.test_date ? new Date(report.test_date).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    {report.scope && (
                      <p className="text-sm text-slate-600">Scope: {report.scope}</p>
                    )}
                  </div>

                  {/* Findings Summary */}
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    <div className="text-center p-2 bg-red-50 rounded">
                      <p className="text-lg font-bold text-red-600">{report.critical_findings || 0}</p>
                      <p className="text-xs text-red-600">Critical</p>
                    </div>
                    <div className="text-center p-2 bg-orange-50 rounded">
                      <p className="text-lg font-bold text-orange-600">{report.high_findings || 0}</p>
                      <p className="text-xs text-orange-600">High</p>
                    </div>
                    <div className="text-center p-2 bg-amber-50 rounded">
                      <p className="text-lg font-bold text-amber-600">{report.medium_findings || 0}</p>
                      <p className="text-xs text-amber-600">Medium</p>
                    </div>
                    <div className="text-center p-2 bg-blue-50 rounded">
                      <p className="text-lg font-bold text-blue-600">{report.low_findings || 0}</p>
                      <p className="text-xs text-blue-600">Low</p>
                    </div>
                  </div>

                  {report.findings_summary && (
                    <p className="text-sm text-slate-600 mb-4">{report.findings_summary}</p>
                  )}

                  <div className="flex items-center gap-2 flex-wrap">
                    {report.has_report_file || report.file_name ? (
                      <Button variant="outline" size="sm" onClick={() => handleDownloadPentestReport(report)}>
                        <Download className="w-4 h-4 mr-2" />
                        Download Report
                      </Button>
                    ) : null}
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setUploadingReport(report);
                          setUploadPentestModalOpen(true);
                        }}
                        data-testid={`pentest-replace-${report.id}`}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {report.has_report_file || report.file_name ? 'Replace File' : 'Upload Report'}
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditPentestModal(report)}
                        data-testid={`pentest-edit-${report.id}`}
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="ghost" size="sm" onClick={() => handleDeletePentestReport(report.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`pentest-delete-${report.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {isAdmin && report.file_name && (
                    <div
                      className="flex items-center justify-between gap-2 pt-2 mt-2 border-t border-gray-100 text-xs text-slate-600"
                      data-testid={`pentest-filename-row-${report.id}`}
                    >
                      <span className="truncate" title={report.file_name}>
                        <FileText className="w-3.5 h-3.5 inline-block mr-1 text-slate-400" />
                        {report.file_name}
                      </span>
                      <button
                        type="button"
                        onClick={() => openRenameFileModal('pentest', report)}
                        className="inline-flex items-center gap-1 text-teal-700 hover:text-teal-800 hover:underline whitespace-nowrap"
                        data-testid={`rename-pentest-file-${report.id}`}
                      >
                        <Pencil className="w-3 h-3" /> Rename
                      </button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Section 2: Compliance Control Coverage */}
      <section>
        <div className="mb-6">
          <h2 className="text-2xl font-heading font-semibold text-black">
            Compliance Control Coverage
          </h2>
          <p className="text-gray-600 mt-1">
            Current compliance status across all frameworks
          </p>
        </div>

        {controlCoverage && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {controlCoverage.frameworks?.map((fw, index) => (
              <Card key={index} className="bg-white border border-gray-200" data-testid={`coverage-${fw.name}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-black">{fw.name}</h3>
                    <span className="text-2xl font-bold text-black">
                      {fw.compliance_percentage}%
                    </span>
                  </div>
                  <Progress value={fw.compliance_percentage} className="h-2 mb-3" />
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="p-2 bg-green-50 rounded">
                      <p className="font-semibold text-green-700">{fw.compliant}</p>
                      <p className="text-green-600">Compliant</p>
                    </div>
                    <div className="p-2 bg-amber-50 rounded">
                      <p className="font-semibold text-amber-700">{fw.partial}</p>
                      <p className="text-amber-600">Partially Compliant</p>
                    </div>
                    <div className="p-2 bg-red-50 rounded">
                      <p className="font-semibold text-red-700">{fw.non_compliant}</p>
                      <p className="text-red-600">Non-Compliant</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    {fw.total} total controls
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Overall Summary */}
        {controlCoverage?.summary && (
          <Card className="mt-4 bg-gray-50 border border-gray-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <BarChart3 className="w-8 h-8 text-black" />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-black">Overall Compliance</h3>
                    <span className="text-xl font-bold text-black">
                      {controlCoverage.summary.overall_percentage}%
                    </span>
                  </div>
                  <Progress value={controlCoverage.summary.overall_percentage} className="h-3" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4 mt-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-black">{controlCoverage.summary.total_controls}</p>
                  <p className="text-xs text-gray-600">Total Controls</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{controlCoverage.summary.compliant}</p>
                  <p className="text-xs text-gray-600">Compliant</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{controlCoverage.summary.partial}</p>
                  <p className="text-xs text-gray-600">Partially Compliant</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{controlCoverage.summary.non_compliant}</p>
                  <p className="text-xs text-gray-600">Non-Compliant</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Section 3: Security Practices */}
      <section>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-heading font-semibold text-black">
              Security Practices
            </h2>
            <p className="text-gray-600 mt-1">
              Our security measures across key areas
            </p>
          </div>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                fetchSecurityPractices();
                setPracticesModalOpen(true);
              }}
              data-testid="edit-practices-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Edit Practices
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {securityPractices
            .filter(p => p.enabled !== false)
            .map((practice, index) => {
              const title = practice.name || practice.category;
              const items = (practice.items || []).filter(it => (typeof it === 'string') || it.enabled !== false);
              return (
                <Card key={practice.id || index} className="bg-white border border-gray-200" data-testid={`security-practice-${title}`}>
                  <CardHeader
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleSection(title)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-100 text-black rounded-lg">
                          {getIconComponent(practice.icon)}
                        </div>
                        <CardTitle className="text-lg text-black">{title}</CardTitle>
                      </div>
                      {expandedSections[title] ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                  </CardHeader>
                  {expandedSections[title] && (
                    <CardContent className="pt-0">
                      <ul className="space-y-2">
                        {items.map((item, idx) => (
                          <li key={item.id || idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span>{typeof item === 'string' ? item : item.text}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  )}
                </Card>
              );
            })}
        </div>
      </section>

      {/* Section 3.5: Policies */}
      <section>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-heading font-semibold text-black">
              Policies
            </h2>
            <p className="text-gray-600 mt-1">
              Our organizational policies and compliance documents
            </p>
          </div>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = '/policies'}
              data-testid="manage-policies-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Manage Policies
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {policies.length === 0 ? (
            <Card className="bg-white border border-gray-200 col-span-full">
              <CardContent className="pt-6 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No policies displayed in Trust Center yet</p>
                {isAdmin && (
                  <p className="text-sm mt-2">
                    Go to <a href="/policies" className="text-blue-600 hover:underline">Policies & Procedures</a> and enable "Show in Trust Center" for policies you want to display here.
                  </p>
                )}
              </CardContent>
            </Card>
          ) : (
            policies.map((policy) => (
              <Card key={policy.id || policy.policy_id} className="bg-white border border-gray-200">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-100 text-green-700">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-medium text-black">{policy.title}</h3>
                        {policy.description && (
                          <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{policy.description}</p>
                        )}
                      </div>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                        Approved
                      </span>
                      {policy.category && (
                        <span className="text-xs text-gray-500">{policy.category}</span>
                      )}
                    </div>
                    {policy.version && (
                      <span className="text-xs text-gray-400">v{policy.version}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </section>

      {/* Section: Risk Profile */}
      {isAdmin && (
        <section>
          <div className="mb-6">
            <h2 className="text-2xl font-heading font-semibold text-black flex items-center gap-2">
              <Shield className="w-6 h-6" />
              Risk Profile
            </h2>
            <p className="text-gray-600 mt-1">
              Define the risk profile for your organization's Trust Center
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Data Access Level */}
            <Card 
              className="bg-white border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => openRiskFieldModal('data_access_level', 'Data Access Level', getRiskProfile().data_access_level?.value)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-100 text-blue-700">
                      <Database className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Data Access Level</p>
                      <p className="font-semibold text-black">{getRiskProfile().data_access_level?.value || 'Public'}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    getRiskProfile().data_access_level?.value === 'Public' ? 'bg-green-100 text-green-700' :
                    getRiskProfile().data_access_level?.value === 'Internal' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {getRiskProfile().data_access_level?.value || 'Public'}
                  </span>
                </div>
                {getRiskProfile().data_access_level?.modified_at && (
                  <p className="text-xs text-gray-400 mt-3">
                    Modified {new Date(getRiskProfile().data_access_level.modified_at).toLocaleDateString()} by {getRiskProfile().data_access_level.modified_by}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Impact Level */}
            <Card 
              className="bg-white border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => openRiskFieldModal('impact_level', 'Impact Level', getRiskProfile().impact_level?.value)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-100 text-amber-700">
                      <AlertTriangle className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Impact Level</p>
                      <p className="font-semibold text-black">{getRiskProfile().impact_level?.value || 'Moderate'}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    getRiskProfile().impact_level?.value === 'Low' ? 'bg-green-100 text-green-700' :
                    getRiskProfile().impact_level?.value === 'Moderate' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {getRiskProfile().impact_level?.value || 'Moderate'}
                  </span>
                </div>
                {getRiskProfile().impact_level?.modified_at && (
                  <p className="text-xs text-gray-400 mt-3">
                    Modified {new Date(getRiskProfile().impact_level.modified_at).toLocaleDateString()} by {getRiskProfile().impact_level.modified_by}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Recovery Time Objective */}
            <Card 
              className="bg-white border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => openRiskFieldModal('recovery_time_objective', 'Recovery Time Objective', getRiskProfile().recovery_time_objective?.value)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-100 text-purple-700">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Recovery Time Objective</p>
                      <p className="font-semibold text-black">{getRiskProfile().recovery_time_objective?.value || '10 hours'}</p>
                    </div>
                  </div>
                </div>
                {getRiskProfile().recovery_time_objective?.modified_at && (
                  <p className="text-xs text-gray-400 mt-3">
                    Modified {new Date(getRiskProfile().recovery_time_objective.modified_at).toLocaleDateString()} by {getRiskProfile().recovery_time_objective.modified_by}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Section 4: Downloadable Certificates */}
      <section>
        <div className="mb-6">
          <h2 className="text-2xl font-heading font-semibold text-black">
            Downloadable Certificates
          </h2>
          <p className="text-gray-600 mt-1">
            Access and download our compliance certificates
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {certifications.filter(c => c.has_certificate).map((cert, index) => (
            <Card key={index} className="hover:shadow-md transition-shadow bg-white border border-gray-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-green-100 text-green-700 rounded-lg">
                    <FileCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-black">{cert.framework} Certificate</h3>
                    <p className="text-xs text-gray-500">
                      {cert.file_type} • {cert.file_size}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full border-gray-300 text-black hover:bg-gray-100"
                  onClick={() => handleDownloadCertificate(cert)}
                  data-testid={`download-doc-${cert.framework}`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Certificate
                </Button>
              </CardContent>
            </Card>
          ))}
          
          {certifications.filter(c => c.has_certificate).length === 0 && (
            <Card className="col-span-full bg-white border border-gray-200">
              <CardContent className="pt-6 text-center py-12">
                <FileCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No certificates available for download yet</p>
                {isAdmin && (
                  <p className="text-sm text-gray-400 mt-2">
                    Upload certificates using the Upload button on each certification card
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Section 5: Custom Domains - Admin Only */}
      {isAdmin && (
        <section>
          <CustomDomainsManager />
        </section>
      )}

      {/* Upload Certificate Modal */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent className="bg-white border border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-black">Upload Certificate</DialogTitle>
            <DialogDescription className="text-gray-600">
              Upload a certificate for {uploadingCert?.framework}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="certificate-file" className="text-black">Certificate File (PDF)</Label>
              <Input
                id="certificate-file"
                type="file"
                accept=".pdf"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="mt-2 bg-white border-gray-300 text-black"
                data-testid="certificate-file-input"
              />
            </div>
            {uploadFile && (
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <FileCheck className="w-5 h-5 text-green-600" />
                <span className="text-sm text-black">{uploadFile.name}</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="ml-auto text-black hover:bg-gray-100"
                  onClick={() => setUploadFile(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadModalOpen(false)} className="border-gray-300 text-black hover:bg-gray-100">
              Cancel
            </Button>
            <Button 
              onClick={handleUploadCertificate} 
              disabled={!uploadFile || uploading}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="confirm-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Certification Modal */}
      <Dialog open={addCertModalOpen} onOpenChange={setAddCertModalOpen}>
        <DialogContent className="bg-white border border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-black">Add Certification</DialogTitle>
            <DialogDescription className="text-gray-600">
              Add a new certification to your Trust Center
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="framework" className="text-black">Framework / Certification</Label>
              <Select 
                value={newCertIsCustom ? '__custom__' : newCert.framework} 
                onValueChange={(value) => {
                  if (value === '__custom__') {
                    setNewCertIsCustom(true);
                    setNewCert({ ...newCert, framework: '' });
                  } else {
                    setNewCertIsCustom(false);
                    setNewCert({ ...newCert, framework: value });
                  }
                }}
              >
                <SelectTrigger className="mt-2 bg-white border-gray-300 text-black" data-testid="framework-select">
                  <SelectValue placeholder="Select framework" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="ISO 27001:2022" className="text-black">ISO 27001:2022</SelectItem>
                  <SelectItem value="SOC 2 Type II" className="text-black">SOC 2 Type II</SelectItem>
                  <SelectItem value="Essential 8" className="text-black">Essential 8</SelectItem>
                  <SelectItem value="PCI-DSS" className="text-black">PCI-DSS</SelectItem>
                  <SelectItem value="HIPAA" className="text-black">HIPAA</SelectItem>
                  <SelectItem value="ISO 42001" className="text-black">ISO 42001</SelectItem>
                  <SelectItem value="GDPR" className="text-black">GDPR</SelectItem>
                  <SelectItem value="NIST CSF" className="text-black">NIST CSF</SelectItem>
                  <SelectItem value="CCPA" className="text-black">CCPA</SelectItem>
                  <SelectItem value="FedRAMP" className="text-black">FedRAMP</SelectItem>
                  <SelectItem value="ISO 22301" className="text-black">ISO 22301</SelectItem>
                  <SelectItem value="CSA STAR" className="text-black">CSA STAR</SelectItem>
                  <SelectItem value="__custom__" className="text-black font-medium">+ Custom framework / certification…</SelectItem>
                </SelectContent>
              </Select>
              {newCertIsCustom && (
                <Input
                  autoFocus
                  value={newCert.framework}
                  onChange={(e) => setNewCert({ ...newCert, framework: e.target.value })}
                  placeholder="e.g., APRA CPS 234, IRAP, MyOwnFramework v1"
                  className="mt-2 bg-white border-gray-300 text-black placeholder:text-gray-400"
                  data-testid="custom-framework-input"
                />
              )}
            </div>
            <div>
              <Label htmlFor="status" className="text-black">Status</Label>
              <Select 
                value={newCert.status} 
                onValueChange={(value) => setNewCert({...newCert, status: value})}
              >
                <SelectTrigger className="mt-2 bg-white border-gray-300 text-black" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="Certified" className="text-black">Certified</SelectItem>
                  <SelectItem value="Compliant" className="text-black">Compliant</SelectItem>
                  <SelectItem value="In Progress" className="text-black">In Progress</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="description" className="text-black">Description</Label>
              <Input
                id="description"
                value={newCert.description}
                onChange={(e) => setNewCert({...newCert, description: e.target.value})}
                placeholder="e.g., Information Security Management System"
                className="mt-2 bg-white border-gray-300 text-black placeholder:text-gray-400"
                data-testid="description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="issued_date" className="text-black">Issue Date</Label>
                <Input
                  id="issued_date"
                  type="date"
                  value={newCert.issued_date}
                  onChange={(e) => setNewCert({...newCert, issued_date: e.target.value})}
                  className="mt-2 bg-white border-gray-300 text-black"
                  data-testid="issued-date-input"
                />
              </div>
              <div>
                <Label htmlFor="expiry_date" className="text-black">Expiry Date</Label>
                <Input
                  id="expiry_date"
                  type="date"
                  value={newCert.expiry_date}
                  onChange={(e) => setNewCert({...newCert, expiry_date: e.target.value})}
                  className="mt-2 bg-white border-gray-300 text-black"
                  data-testid="expiry-date-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCertModalOpen(false)} className="border-gray-300 text-black hover:bg-gray-100">
              Cancel
            </Button>
            <Button onClick={handleAddCertification} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="confirm-add-cert-btn">
              <Award className="w-4 h-4 mr-2" />
              Add Certification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Certification Modal */}
      <Dialog open={editCertModalOpen} onOpenChange={setEditCertModalOpen}>
        <DialogContent className="bg-white border border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-black">Edit Certification</DialogTitle>
            <DialogDescription className="text-gray-600">
              Update certification details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-framework" className="text-black">Framework / Certification</Label>
              <Select 
                value={editCertIsCustom ? '__custom__' : (editingCert?.framework || '')} 
                onValueChange={(value) => {
                  if (value === '__custom__') {
                    setEditCertIsCustom(true);
                    setEditingCert({ ...editingCert, framework: '' });
                  } else {
                    setEditCertIsCustom(false);
                    setEditingCert({ ...editingCert, framework: value });
                  }
                }}
              >
                <SelectTrigger className="mt-2 bg-white border-gray-300 text-black" data-testid="edit-framework-select">
                  <SelectValue placeholder="Select framework" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="ISO 27001:2022" className="text-black">ISO 27001:2022</SelectItem>
                  <SelectItem value="SOC 2 Type II" className="text-black">SOC 2 Type II</SelectItem>
                  <SelectItem value="Essential 8" className="text-black">Essential 8</SelectItem>
                  <SelectItem value="PCI-DSS" className="text-black">PCI-DSS</SelectItem>
                  <SelectItem value="HIPAA" className="text-black">HIPAA</SelectItem>
                  <SelectItem value="ISO 42001" className="text-black">ISO 42001</SelectItem>
                  <SelectItem value="GDPR" className="text-black">GDPR</SelectItem>
                  <SelectItem value="NIST CSF" className="text-black">NIST CSF</SelectItem>
                  <SelectItem value="CCPA" className="text-black">CCPA</SelectItem>
                  <SelectItem value="FedRAMP" className="text-black">FedRAMP</SelectItem>
                  <SelectItem value="ISO 22301" className="text-black">ISO 22301</SelectItem>
                  <SelectItem value="CSA STAR" className="text-black">CSA STAR</SelectItem>
                  <SelectItem value="__custom__" className="text-black font-medium">+ Custom framework / certification…</SelectItem>
                </SelectContent>
              </Select>
              {editCertIsCustom && (
                <Input
                  autoFocus
                  value={editingCert?.framework || ''}
                  onChange={(e) => setEditingCert({ ...editingCert, framework: e.target.value })}
                  placeholder="e.g., APRA CPS 234, IRAP, MyOwnFramework v1"
                  className="mt-2 bg-white border-gray-300 text-black placeholder:text-gray-400"
                  data-testid="edit-custom-framework-input"
                />
              )}
            </div>
            <div>
              <Label htmlFor="edit-status" className="text-black">Status</Label>
              <Select 
                value={editingCert?.status || 'In Progress'} 
                onValueChange={(value) => setEditingCert({...editingCert, status: value})}
              >
                <SelectTrigger className="mt-2 bg-white border-gray-300 text-black" data-testid="edit-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="Certified" className="text-black">Certified</SelectItem>
                  <SelectItem value="Compliant" className="text-black">Compliant</SelectItem>
                  <SelectItem value="In Progress" className="text-black">In Progress</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-description" className="text-black">Description</Label>
              <Input
                id="edit-description"
                value={editingCert?.description || ''}
                onChange={(e) => setEditingCert({...editingCert, description: e.target.value})}
                placeholder="e.g., Information Security Management System"
                className="mt-2 bg-white border-gray-300 text-black placeholder:text-gray-400"
                data-testid="edit-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-issued_date" className="text-black">Issue Date</Label>
                <Input
                  id="edit-issued_date"
                  type="date"
                  value={editingCert?.issued_date || ''}
                  onChange={(e) => setEditingCert({...editingCert, issued_date: e.target.value})}
                  className="mt-2 bg-white border-gray-300 text-black"
                  data-testid="edit-issued-date-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-expiry_date" className="text-black">Expiry Date</Label>
                <Input
                  id="edit-expiry_date"
                  type="date"
                  value={editingCert?.expiry_date || ''}
                  onChange={(e) => setEditingCert({...editingCert, expiry_date: e.target.value})}
                  className="mt-2 bg-white border-gray-300 text-black"
                  data-testid="edit-expiry-date-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditCertModalOpen(false); setEditingCert(null); }} className="border-gray-300 text-black hover:bg-gray-100">
              Cancel
            </Button>
            <Button onClick={handleEditCertification} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="confirm-edit-cert-btn">
              <Award className="w-4 h-4 mr-2" />
              Update Certification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Risk Profile Edit Modal */}
      <Dialog open={riskProfileModalOpen} onOpenChange={setRiskProfileModalOpen}>
        <DialogContent className="max-w-md bg-white text-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Edit {editingRiskField?.label}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {editingRiskField?.key === 'data_access_level' && 'What type of data does this product have access to?'}
              {editingRiskField?.key === 'impact_level' && 'What is the potential impact if this system is compromised?'}
              {editingRiskField?.key === 'recovery_time_objective' && 'How quickly must the system be restored after an outage?'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {editingRiskField?.key === 'data_access_level' && (
              <div className="space-y-3">
                {['Public', 'Internal', 'Restricted'].map((option) => (
                  <div 
                    key={option}
                    onClick={() => setRiskFieldValue(option)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      riskFieldValue === option 
                        ? 'border-slate-900 bg-slate-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{option}</p>
                        <p className="text-sm text-slate-500">
                          {option === 'Public' && 'Data that is publicly available'}
                          {option === 'Internal' && 'Data restricted to internal employees'}
                          {option === 'Restricted' && 'Highly sensitive data with limited access'}
                        </p>
                      </div>
                      {riskFieldValue === option && (
                        <CheckCircle className="w-5 h-5 text-slate-900" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {editingRiskField?.key === 'impact_level' && (
              <div className="space-y-3">
                {['Low', 'Moderate', 'High'].map((option) => (
                  <div 
                    key={option}
                    onClick={() => setRiskFieldValue(option)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      riskFieldValue === option 
                        ? 'border-slate-900 bg-slate-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{option}</p>
                        <p className="text-sm text-slate-500">
                          {option === 'Low' && 'Minimal business impact if compromised'}
                          {option === 'Moderate' && 'Significant but recoverable business impact'}
                          {option === 'High' && 'Critical business impact requiring immediate response'}
                        </p>
                      </div>
                      {riskFieldValue === option && (
                        <CheckCircle className="w-5 h-5 text-slate-900" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {editingRiskField?.key === 'recovery_time_objective' && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="rto" className="text-slate-700">Recovery Time Objective</Label>
                  <Input
                    id="rto"
                    value={riskFieldValue}
                    onChange={(e) => setRiskFieldValue(e.target.value)}
                    placeholder="e.g., 10 hours, 24 hours, 4 hours"
                    className="mt-2 bg-white border-gray-300 text-slate-900"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Enter the maximum acceptable downtime for your systems
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {['1 hour', '4 hours', '10 hours', '24 hours', '48 hours', '72 hours'].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setRiskFieldValue(preset)}
                      className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                        riskFieldValue === preset
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-700 border-gray-300 hover:border-slate-400'
                      }`}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setRiskProfileModalOpen(false); setEditingRiskField(null); }} 
              className="border-gray-300 text-slate-900"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveRiskField} 
              disabled={savingRiskProfile || !riskFieldValue}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {savingRiskProfile ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Public Trust Center Settings Modal */}
      <Dialog open={settingsModalOpen} onOpenChange={setSettingsModalOpen}>
        <DialogContent className="max-w-2xl bg-white text-slate-900 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Public Trust Center Settings
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Configure your public Trust Center page that can be shared with customers and partners.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Section 1: Access & Visibility */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Access & Visibility
              </h3>
              <div className="space-y-4">
                {/* Enable Public Access */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-slate-900 font-medium">Enable Public Access</Label>
                    <p className="text-sm text-slate-500">Make your Trust Center publicly accessible</p>
                  </div>
                  <Switch
                    checked={editedSettings.is_public || false}
                    onCheckedChange={(checked) => setEditedSettings({...editedSettings, is_public: checked})}
                    data-testid="public-access-switch"
                  />
                </div>
                
                {/* URL Slug */}
                <div>
                  <Label htmlFor="url_slug" className="text-slate-700 text-sm">Public URL Slug</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-slate-500">/trust/</span>
                    <Input
                      id="url_slug"
                      value={editedSettings.public_slug || ''}
                      onChange={(e) => setEditedSettings({...editedSettings, public_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')})}
                      placeholder="your-company"
                      className="bg-white border-slate-300 text-slate-900"
                      data-testid="url-slug-input"
                    />
                  </div>
                  {publicSettings?.public_url && (
                    <p className="text-xs text-slate-500 mt-1">
                      Full URL: {publicSettings.public_url}
                    </p>
                  )}
                </div>
                
                {/* Visible Sections */}
                <div>
                  <Label className="text-slate-700 text-sm mb-2 block">Visible Sections</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'certifications', label: 'Certifications' },
                      { key: 'compliance_coverage', label: 'Compliance Coverage' },
                      { key: 'security_practices', label: 'Security Practices' },
                      { key: 'policies', label: 'Policies' },
                      { key: 'documents', label: 'Public Documents' },
                      { key: 'pentest_reports', label: 'Pentest Reports' },
                      { key: 'risk_profile', label: 'Risk Profile' },
                      { key: 'product_security', label: 'Product Security' },
                      { key: 'trusted_by', label: 'Trusted By Section' }
                    ].map((section) => (
                      <div key={section.key} className="flex items-center gap-2">
                        <Switch
                          checked={editedSettings.visible_sections?.[section.key] ?? true}
                          onCheckedChange={(checked) => setEditedSettings({
                            ...editedSettings,
                            visible_sections: {
                              ...editedSettings.visible_sections,
                              [section.key]: checked
                            }
                          })}
                          className="scale-75"
                          data-testid={`section-${section.key}-switch`}
                        />
                        <span className="text-xs text-slate-600">{section.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Branding */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Branding
              </h3>
              <div className="space-y-4">
                {/* Organization Name */}
                <div>
                  <Label htmlFor="org_name" className="text-slate-700 text-sm">Organization Name</Label>
                  <Input
                    id="org_name"
                    value={editedSettings.organization_name || ''}
                    onChange={(e) => setEditedSettings({...editedSettings, organization_name: e.target.value})}
                    placeholder="Your Organization Name"
                    className="mt-1 bg-white border-slate-300 text-slate-900"
                    data-testid="org-name-input"
                  />
                </div>

                {/* Hero Title */}
                <div>
                  <Label htmlFor="hero_title" className="text-slate-700 text-sm">Welcome Title</Label>
                  <Input
                    id="hero_title"
                    value={editedSettings.hero_title || ''}
                    onChange={(e) => setEditedSettings({...editedSettings, hero_title: e.target.value})}
                    placeholder={`Welcome to ${editedSettings.organization_name || 'Company Name'}`}
                    className="mt-1 bg-white border-slate-300 text-slate-900"
                    data-testid="hero-title-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave empty to use "Welcome to [Organization Name]"</p>
                </div>

                {/* Hero Subtitle */}
                <div>
                  <Label htmlFor="hero_subtitle" className="text-slate-700 text-sm">Welcome Subtitle</Label>
                  <Input
                    id="hero_subtitle"
                    value={editedSettings.hero_subtitle || ''}
                    onChange={(e) => setEditedSettings({...editedSettings, hero_subtitle: e.target.value})}
                    placeholder="Access our security documentation and compliance reports"
                    className="mt-1 bg-white border-slate-300 text-slate-900"
                    data-testid="hero-subtitle-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave empty for default subtitle</p>
                </div>

                {/* Organization Logo */}
                <div>
                  <Label className="text-slate-700 text-sm">Organization Logo</Label>
                  <p className="text-xs text-slate-500 mb-2">Upload your company logo (PNG, JPG, SVG, or WebP - max 2MB)</p>
                  
                  <div className="flex items-start gap-3">
                    {/* Logo Preview */}
                    <div className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center bg-white overflow-hidden">
                      {editedSettings.organization_logo ? (
                        <img 
                          src={editedSettings.organization_logo} 
                          alt="Logo" 
                          className="w-full h-full object-contain p-1"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="text-center">
                          <Upload className="w-6 h-6 text-slate-400 mx-auto" />
                          <span className="text-xs text-slate-400">No logo</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Upload Button */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                            onChange={handleLogoUpload}
                            className="hidden"
                            data-testid="logo-file-input"
                          />
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 cursor-pointer">
                            {uploadingLogo ? (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="w-3 h-3" />
                                Upload Logo
                              </>
                            )}
                          </span>
                        </label>
                        {editedSettings.organization_logo && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-xs text-red-600 hover:text-red-700 h-7"
                            onClick={() => setEditedSettings({...editedSettings, organization_logo: null})}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-slate-400">Or enter URL:</p>
                      <Input
                        value={editedSettings.organization_logo?.startsWith('data:') ? '' : (editedSettings.organization_logo || '')}
                        onChange={(e) => setEditedSettings({...editedSettings, organization_logo: e.target.value})}
                        placeholder="https://example.com/logo.png"
                        className="bg-white border-slate-300 text-slate-900 text-xs h-8"
                        data-testid="org-logo-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Content */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Content
              </h3>
              <div className="space-y-4">
                {/* Welcome Message */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="custom_message" className="text-slate-700 text-sm">Welcome Message</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs text-teal-600 hover:text-teal-700 h-6"
                      onClick={() => {
                        const defaultMsg = `Welcome to ${editedSettings.organization_name || 'Our'}'s Trust Center. We are committed to maintaining the highest standards of security, privacy, and compliance. This portal provides transparency into our security posture and compliance certifications. Feel free to explore our certifications, compliance coverage, and security practices.`;
                        setEditedSettings({...editedSettings, custom_message: defaultMsg});
                      }}
                      data-testid="generate-default-message-btn"
                    >
                      Generate Default
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mb-1">This message appears in the Overview section</p>
                  <Textarea
                    id="custom_message"
                    value={editedSettings.custom_message || ''}
                    onChange={(e) => setEditedSettings({...editedSettings, custom_message: e.target.value})}
                    placeholder="We are committed to maintaining the highest standards of security..."
                    className="bg-white border-slate-300 text-slate-900 min-h-[80px]"
                    data-testid="custom-message-input"
                  />
                </div>

                {/* Contact Email */}
                <div>
                  <Label htmlFor="contact_email" className="text-slate-700 text-sm">Contact Email</Label>
                  <p className="text-xs text-slate-500 mb-1">Displayed for security inquiries</p>
                  <Input
                    id="contact_email"
                    type="email"
                    value={editedSettings.contact_email || ''}
                    onChange={(e) => setEditedSettings({...editedSettings, contact_email: e.target.value})}
                    placeholder="security@yourcompany.com"
                    className="bg-white border-slate-300 text-slate-900"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
            </div>

            {/* Section 4: Custom Domain */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Link className="w-4 h-4" />
                Custom Domain (Optional)
              </h3>
              <p className="text-xs text-slate-500 mb-3">
                Serve your Trust Center from your own subdomain (e.g., trust.yourcompany.com)
              </p>
              
              <div className="space-y-3">
                <div>
                  <Label htmlFor="custom_domain" className="text-slate-700 text-sm">Your Custom Domain</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      id="custom_domain"
                      value={editedSettings.custom_domain || ''}
                      onChange={(e) => {
                        setEditedSettings({...editedSettings, custom_domain: e.target.value});
                        setDomainVerificationResult(null);
                      }}
                      placeholder="trust.yourcompany.com"
                      className="bg-white border-slate-300 text-slate-900"
                      data-testid="custom-domain-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleVerifyDomain}
                      disabled={verifyingDomain || !editedSettings.custom_domain}
                      className="border-slate-300 text-slate-900 whitespace-nowrap"
                      data-testid="verify-domain-btn"
                    >
                      {verifyingDomain ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Verify
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* CNAME Instructions */}
                {editedSettings.custom_domain && (
                  <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <h4 className="text-xs font-medium text-slate-900 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-3 h-3 text-amber-500" />
                      DNS Configuration Required
                    </h4>
                    <p className="text-xs text-slate-600 mb-2">
                      Add a CNAME record in your DNS provider:
                    </p>
                    <div className="bg-slate-50 rounded p-2 space-y-1 text-xs font-mono">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Type:</span>
                        <span className="text-slate-900">CNAME</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Name:</span>
                        <span className="text-slate-900">{editedSettings.custom_domain?.split('.')[0] || 'trust'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">Target:</span>
                        <div className="flex items-center gap-1">
                          <span className="text-slate-900">{editedSettings.cname_target || publicSettings?.cname_target || 'compliance.cyberforte.com.au'}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0"
                            onClick={() => {
                              navigator.clipboard.writeText(editedSettings.cname_target || publicSettings?.cname_target || 'compliance.cyberforte.com.au');
                              toast.success('Copied!');
                            }}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Hosting Note */}
                    <div className="mt-3 p-2 bg-amber-50 rounded border border-amber-100">
                      <h5 className="text-xs font-medium text-amber-900 mb-1">Important Note</h5>
                      <p className="text-xs text-amber-700">
                        Custom domains require SSL configuration on the hosting platform. 
                        Contact your hosting provider to add your custom domain to their SSL certificate.
                      </p>
                    </div>
                  </div>
                )}

                {/* Verification Status */}
                {editedSettings.custom_domain && (
                  <div className={`flex items-center gap-2 p-2 rounded text-xs ${
                    publicSettings?.custom_domain_verified || domainVerificationResult?.verified
                      ? 'bg-green-50 text-green-700'
                      : domainVerificationResult?.verified === false
                        ? 'bg-red-50 text-red-700'
                        : 'bg-amber-50 text-amber-700'
                  }`}>
                    {publicSettings?.custom_domain_verified || domainVerificationResult?.verified ? (
                      <>
                        <CheckCircle className="w-3 h-3" />
                        <span>Domain verified</span>
                      </>
                    ) : domainVerificationResult?.verified === false ? (
                      <>
                        <AlertCircle className="w-3 h-3" />
                        <span>{domainVerificationResult.message}</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-3 h-3" />
                        <span>Pending verification</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsModalOpen(false)} className="border-slate-300 text-slate-900">
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSettings} 
              disabled={savingSettings}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-settings-btn"
            >
              {savingSettings ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Pentest Report Modal */}
      <Dialog open={addPentestModalOpen} onOpenChange={setAddPentestModalOpen}>
        <DialogContent className="max-w-lg bg-white text-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <FileSearch className="w-5 h-5" />
              Add Penetration Test Report
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Add a new pentest report to your Trust Center
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="pentest_title" className="text-slate-900">Report Title</Label>
              <Input
                id="pentest_title"
                value={newPentest.title}
                onChange={(e) => setNewPentest({...newPentest, title: e.target.value})}
                placeholder="Q4 2024 External Penetration Test"
                className="mt-2 bg-white border-slate-300 text-slate-900"
                data-testid="pentest-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pentest_vendor" className="text-slate-900">Testing Vendor</Label>
                <Input
                  id="pentest_vendor"
                  value={newPentest.vendor}
                  onChange={(e) => setNewPentest({...newPentest, vendor: e.target.value})}
                  placeholder="SecureTech Inc."
                  className="mt-2 bg-white border-slate-300 text-slate-900"
                  data-testid="pentest-vendor-input"
                />
              </div>
              <div>
                <Label htmlFor="pentest_date" className="text-slate-900">Test Date</Label>
                <Input
                  id="pentest_date"
                  type="date"
                  value={newPentest.test_date}
                  onChange={(e) => setNewPentest({...newPentest, test_date: e.target.value})}
                  className="mt-2 bg-white border-slate-300 text-slate-900"
                  data-testid="pentest-date-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="pentest_scope" className="text-slate-900">Scope</Label>
              <Input
                id="pentest_scope"
                value={newPentest.scope}
                onChange={(e) => setNewPentest({...newPentest, scope: e.target.value})}
                placeholder="External network, web applications"
                className="mt-2 bg-white border-slate-300 text-slate-900"
                data-testid="pentest-scope-input"
              />
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-slate-900 text-xs">Critical</Label>
                <Input
                  type="number"
                  min="0"
                  value={newPentest.critical_findings}
                  onChange={(e) => setNewPentest({...newPentest, critical_findings: parseInt(e.target.value) || 0})}
                  className="mt-1 bg-white border-slate-300 text-slate-900"
                  data-testid="pentest-critical-input"
                />
              </div>
              <div>
                <Label className="text-slate-900 text-xs">High</Label>
                <Input
                  type="number"
                  min="0"
                  value={newPentest.high_findings}
                  onChange={(e) => setNewPentest({...newPentest, high_findings: parseInt(e.target.value) || 0})}
                  className="mt-1 bg-white border-slate-300 text-slate-900"
                  data-testid="pentest-high-input"
                />
              </div>
              <div>
                <Label className="text-slate-900 text-xs">Medium</Label>
                <Input
                  type="number"
                  min="0"
                  value={newPentest.medium_findings}
                  onChange={(e) => setNewPentest({...newPentest, medium_findings: parseInt(e.target.value) || 0})}
                  className="mt-1 bg-white border-slate-300 text-slate-900"
                  data-testid="pentest-medium-input"
                />
              </div>
              <div>
                <Label className="text-slate-900 text-xs">Low</Label>
                <Input
                  type="number"
                  min="0"
                  value={newPentest.low_findings}
                  onChange={(e) => setNewPentest({...newPentest, low_findings: parseInt(e.target.value) || 0})}
                  className="mt-1 bg-white border-slate-300 text-slate-900"
                  data-testid="pentest-low-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="pentest_summary" className="text-slate-900">Findings Summary</Label>
              <Textarea
                id="pentest_summary"
                value={newPentest.findings_summary}
                onChange={(e) => setNewPentest({...newPentest, findings_summary: e.target.value})}
                placeholder="Summary of key findings and remediation status..."
                className="mt-2 bg-white border-slate-300 text-slate-900 min-h-[80px]"
                data-testid="pentest-summary-input"
              />
            </div>
            <div>
              <Label htmlFor="pentest_status" className="text-slate-900">Status</Label>
              <Select
                value={newPentest.status}
                onValueChange={(value) => setNewPentest({...newPentest, status: value})}
              >
                <SelectTrigger className="mt-2 bg-white border-slate-300" data-testid="pentest-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Remediation Pending">Remediation Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddPentestModalOpen(false)} className="border-slate-300 text-slate-900">
              Cancel
            </Button>
            <Button 
              onClick={handleAddPentestReport} 
              disabled={!newPentest.title || !newPentest.vendor}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-pentest-btn"
            >
              <FileSearch className="w-4 h-4 mr-2" />
              Add Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Pentest Report File Modal */}
      <Dialog open={uploadPentestModalOpen} onOpenChange={setUploadPentestModalOpen}>
        <DialogContent className="max-w-md bg-white text-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-900">
              {uploadingReport?.has_report_file || uploadingReport?.file_name ? 'Replace Report File' : 'Upload Report File'}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {uploadingReport?.has_report_file || uploadingReport?.file_name
                ? `Replace the current PDF for: ${uploadingReport?.title}`
                : `Upload the PDF report for: ${uploadingReport?.title}`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="file"
              accept=".pdf"
              onChange={(e) => setPentestFile(e.target.files?.[0] || null)}
              className="bg-white border-slate-300"
              data-testid="pentest-file-input"
            />
            <p className="text-xs text-slate-500 mt-2">Only PDF files are accepted</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setUploadPentestModalOpen(false);
              setPentestFile(null);
            }} className="border-slate-300 text-slate-900">
              Cancel
            </Button>
            <Button 
              onClick={handleUploadPentestFile} 
              disabled={!pentestFile || uploadingPentest}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="upload-pentest-btn"
            >
              {uploadingPentest ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Pentest Report Modal */}
      <Dialog open={editPentestModalOpen} onOpenChange={setEditPentestModalOpen}>
        <DialogContent className="max-w-lg bg-white text-slate-900 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Edit Penetration Test Report
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Update the report details. File replacement is handled separately from the card.
            </DialogDescription>
          </DialogHeader>
          {editingPentest && (
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit_pentest_title" className="text-slate-900">Report Title</Label>
                <Input
                  id="edit_pentest_title"
                  value={editingPentest.title || ''}
                  onChange={(e) => setEditingPentest({ ...editingPentest, title: e.target.value })}
                  className="mt-2 bg-white border-slate-300 text-slate-900"
                  data-testid="edit-pentest-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_pentest_vendor" className="text-slate-900">Testing Vendor</Label>
                  <Input
                    id="edit_pentest_vendor"
                    value={editingPentest.vendor || ''}
                    onChange={(e) => setEditingPentest({ ...editingPentest, vendor: e.target.value })}
                    className="mt-2 bg-white border-slate-300 text-slate-900"
                    data-testid="edit-pentest-vendor-input"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_pentest_date" className="text-slate-900">Test Date</Label>
                  <Input
                    id="edit_pentest_date"
                    type="date"
                    value={editingPentest.test_date || ''}
                    onChange={(e) => setEditingPentest({ ...editingPentest, test_date: e.target.value })}
                    className="mt-2 bg-white border-slate-300 text-slate-900"
                    data-testid="edit-pentest-date-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="edit_pentest_scope" className="text-slate-900">Scope</Label>
                <Input
                  id="edit_pentest_scope"
                  value={editingPentest.scope || ''}
                  onChange={(e) => setEditingPentest({ ...editingPentest, scope: e.target.value })}
                  className="mt-2 bg-white border-slate-300 text-slate-900"
                  data-testid="edit-pentest-scope-input"
                />
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label className="text-slate-900 text-xs">Critical</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editingPentest.critical_findings ?? 0}
                    onChange={(e) => setEditingPentest({ ...editingPentest, critical_findings: parseInt(e.target.value) || 0 })}
                    className="mt-1 bg-white border-slate-300 text-slate-900"
                    data-testid="edit-pentest-critical-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-900 text-xs">High</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editingPentest.high_findings ?? 0}
                    onChange={(e) => setEditingPentest({ ...editingPentest, high_findings: parseInt(e.target.value) || 0 })}
                    className="mt-1 bg-white border-slate-300 text-slate-900"
                    data-testid="edit-pentest-high-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-900 text-xs">Medium</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editingPentest.medium_findings ?? 0}
                    onChange={(e) => setEditingPentest({ ...editingPentest, medium_findings: parseInt(e.target.value) || 0 })}
                    className="mt-1 bg-white border-slate-300 text-slate-900"
                    data-testid="edit-pentest-medium-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-900 text-xs">Low</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editingPentest.low_findings ?? 0}
                    onChange={(e) => setEditingPentest({ ...editingPentest, low_findings: parseInt(e.target.value) || 0 })}
                    className="mt-1 bg-white border-slate-300 text-slate-900"
                    data-testid="edit-pentest-low-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="edit_pentest_summary" className="text-slate-900">Findings Summary</Label>
                <Textarea
                  id="edit_pentest_summary"
                  value={editingPentest.findings_summary || ''}
                  onChange={(e) => setEditingPentest({ ...editingPentest, findings_summary: e.target.value })}
                  className="mt-2 bg-white border-slate-300 text-slate-900 min-h-[80px]"
                  data-testid="edit-pentest-summary-input"
                />
              </div>
              <div>
                <Label htmlFor="edit_pentest_status" className="text-slate-900">Status</Label>
                <Select
                  value={editingPentest.status || 'Completed'}
                  onValueChange={(value) => setEditingPentest({ ...editingPentest, status: value })}
                >
                  <SelectTrigger className="mt-2 bg-white border-slate-300" data-testid="edit-pentest-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Remediation Pending">Remediation Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editingPentest.file_name && (
                <div className="text-xs text-slate-500 flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5" />
                  Current file: <span className="text-slate-700 truncate">{editingPentest.file_name}</span>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setEditPentestModalOpen(false); setEditingPentest(null); }}
              className="border-slate-300 text-slate-900"
              data-testid="cancel-edit-pentest-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditPentestReport}
              disabled={savingEditPentest || !editingPentest?.title || !editingPentest?.vendor}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-edit-pentest-btn"
            >
              {savingEditPentest ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Pencil className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename File Modal (used by both certifications and pentest reports) */}
      <Dialog open={!!renameFileModal} onOpenChange={(open) => { if (!open) { setRenameFileModal(null); setRenameFileValue(''); } }}>
        <DialogContent className="max-w-md bg-white text-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Rename File
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Update the visible file name for {renameFileModal?.label || 'this record'}. The actual file contents remain unchanged.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="rename_file_value" className="text-slate-900">File name</Label>
            <Input
              id="rename_file_value"
              value={renameFileValue}
              onChange={(e) => setRenameFileValue(e.target.value)}
              placeholder="e.g., iso-27001-certificate-2025.pdf"
              className="mt-2 bg-white border-slate-300 text-slate-900"
              data-testid="rename-file-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setRenameFileModal(null); setRenameFileValue(''); }}
              className="border-slate-300 text-slate-900"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRenameFile}
              disabled={savingRename || !renameFileValue.trim()}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="confirm-rename-file-btn"
            >
              {savingRename ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Pencil className="w-4 h-4 mr-2" />}
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Security Practices Management Modal */}
      <Dialog open={practicesModalOpen} onOpenChange={setPracticesModalOpen}>
        <DialogContent className="max-w-3xl bg-white text-slate-900 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Manage Security Practices
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Customize the security practices shown on your public Trust Center. Toggle items on/off to show only what applies to your organization.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {editablePractices.map((category, categoryIndex) => (
              <div key={category.id} className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3 flex-1">
                    <Switch
                      checked={category.enabled}
                      onCheckedChange={(checked) => {
                        setEditablePractices(editablePractices.map(p => 
                          p.id === category.id ? {...p, enabled: checked} : p
                        ));
                      }}
                    />
                    <Input
                      value={category.name}
                      onChange={(e) => {
                        setEditablePractices(editablePractices.map(p => 
                          p.id === category.id ? {...p, name: e.target.value} : p
                        ));
                      }}
                      className="font-medium bg-white border-slate-300 text-slate-900 max-w-xs"
                      placeholder="Category Name"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => deletePracticeCategory(category.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-2 ml-10">
                  {category.items?.map((item, itemIndex) => (
                    <div key={item.id} className="flex items-center gap-2">
                      <Switch
                        checked={item.enabled}
                        onCheckedChange={(checked) => updatePracticeItem(category.id, item.id, { enabled: checked })}
                        className="scale-75"
                      />
                      <Input
                        value={item.text}
                        onChange={(e) => updatePracticeItem(category.id, item.id, { text: e.target.value })}
                        className="flex-1 bg-white border-slate-300 text-slate-900 text-sm"
                        placeholder="Security practice description"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-slate-400 hover:text-red-600 h-7 w-7 p-0"
                        onClick={() => deletePracticeItem(category.id, item.id)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-teal-600 hover:text-teal-700 text-xs"
                    onClick={() => addPracticeItem(category.id)}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Item
                  </Button>
                </div>
              </div>
            ))}
            
            <Button
              type="button"
              variant="outline"
              className="w-full border-dashed border-slate-300 text-slate-600"
              onClick={addPracticeCategory}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPracticesModalOpen(false)} className="border-slate-300 text-slate-900">
              Cancel
            </Button>
            <Button 
              onClick={handleSavePractices} 
              disabled={savingPractices}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-practices-btn"
            >
              {savingPractices ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Practices'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
